import { Controller } from "@hotwired/stimulus";
import { setupPlayer } from "../../../javascripts/videoPlayer/video";

export default class extends Controller {
  static values = {
    trapFocus: {
      type: Boolean,
      default: true,
    },
  };

  declare target: HTMLElement;
  declare modal: HTMLDialogElement | null;
  declare closeButton: HTMLButtonElement | null;
  declare video: Element | null;
  declare trapFocusValue: boolean;

  connect() {
    this.modal = null;
    this.closeButton = null;
  }

  open(event: Event) {
    const target = event.target as HTMLElement;

    this.target = target;

    const player = this.element.querySelector(
      `template[data-course-id='${target.dataset.id}']`
    ) as HTMLTemplateElement;

    if (!this.modal) {
      this.createModal(player);
    }

    this.modal.showModal();
    this.modal.classList.add("video-modal");
    this.modal.setAttribute("data-type", "video");

    if (this.trapFocusValue) {
      this.modal.dataset.controller = "focus-trap";
    }

    // Prevent body scroll
    document.documentElement.style.overflow = "hidden";

    if (player) {
      setupPlayer(this.video);
    } else {
      // eslint-disable-next-line no-console
      console.error("No player found for the given course ID.");
    }
  }

  createModal(player: HTMLTemplateElement): void {
    const templateContent = player.content.cloneNode(true);

    this.modal = document.createElement("dialog");

    this.modal.appendChild(templateContent);

    this.modal.addEventListener("click", (event: MouseEvent) => {
      if (event.target === this.modal) {
        this.close();
      }
    });

    document.body.appendChild(this.modal);

    this.video = this.modal.querySelector("video-js");

    this.closeButton = this.modal.querySelector(
      '[data-element="close-button"]'
    ) as HTMLButtonElement;

    // Add event listeners to modal elements
    this.setupModalEventListeners();
  }

  destroyModal(): void {
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal);
      this.modal = null;
      this.video = null;
      this.closeButton = null;
    }
  }

  close(): void {
    if (!this.modal) return;

    // Close modal using the native dialog API
    if (this.modal instanceof HTMLDialogElement) {
      this.modal.close();
    }

    // Perform cleanup
    this.performCleanup();
  }

  private performCleanup(): void {
    if (!this.modal) return;

    this.pauseAndResetVideo();
    this.modal.removeAttribute("data-type");
    this.restoreBodyScroll();
    this.destroyModal();
  }

  setupModalEventListeners(): void {
    this.closeButton.addEventListener("click", () => this.close());

    // Listen for native dialog close events (e.g., Escape key)
    this.modal.addEventListener("close", () => {
      // The dialog was closed natively, just perform cleanup
      this.performCleanup();
    });
  }

  restoreBodyScroll(): void {
    document.documentElement.style.overflow = "";
  }

  pauseAndResetVideo(): void {
    if (this.video) {
      const videoEl = this.video as HTMLVideoElement;
      if (videoEl.pause) {
        videoEl.pause();
        videoEl.currentTime = 0;
      }
    }
  }
}
